import { FileText } from "lucide-react";
import { BiSolidFileTxt } from "react-icons/bi";
import { CiImageOn } from "react-icons/ci";
import { FaFilePdf } from "react-icons/fa";
import { SiAutocad, SiGoogledocs, SiGooglesheets } from "react-icons/si";


export const INDIAN_STATES = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar",
  "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka",
  "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
  "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
];
// A function to format dates in 'YYYY-MM-DD' format
export const formatDate = (date) => {
  const d = new Date(date);
  const month = "" + (d.getMonth() + 1);
  const day = "" + d.getDate();
  const year = d.getFullYear();

  return [year, month.padStart(2, "0"), day.padStart(2, "0")].join("-");
};

// export date in 28 Nov 2023 format
export const formatDateLong = (date) => {
  const d = new Date(date);
  const options = { year: "numeric", month: "short", day: "numeric" };
  return d.toLocaleDateString("en-IN", options);
};

// export date in 7:37 pm, 28 Nov 2023 format
export const formatDateTimeLong = (date) => {
  const d = new Date(date);
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return d.toLocaleDateString("en-IN", options);
};

// export date in 7:37 pm if date < currentDate - 1min else show min ago
export const formatDateTimeShort = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const now = new Date();
  const diffInSeconds = Math.floor((now - d) / 1000);

  if (diffInSeconds > 60) {
    return d.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  } else {
    return "Just now";
  }
};



export const formatCurrency = (amount) => {
  if (isNaN(amount)) return amount;

  const options = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  };

  return Number(amount).toLocaleString("en-IN", options);
}

// 23567.6 -> 23.5k , 23000 -> 23k , 23500 -> 23.5k, 50,00,000 -> 50L, 50,00,200 -> 50L
export const formatCurrencyShort = (amount) => {
  if (isNaN(amount)) return amount;

  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)}Cr`;
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)}L`;
  }
  if (amount >= 1000) {
    return `₹${(amount / 1000).toFixed(2)}k`;
  }

  return `₹${amount?.toLocaleString()}`;
}

export const fileTypeIcons = ({ fileType, className }) => {
  switch (fileType) {
    case "png":
      return <CiImageOn size={24} className={className} />;
    case "jpg":
      return <CiImageOn size={24} className={className} />;
    case "jpeg":
      return <CiImageOn size={24} className={className} />;
    case "pdf":
      return <FaFilePdf size={24} className={className} />;
    case "doc":
      return <SiGoogledocs size={24} className={className} />;
    case "docx":
      return <SiGoogledocs size={24} className={className} />;
    case "txt":
      return <BiSolidFileTxt size={24} className={className} />;
    case "xlsx":
      return <SiGooglesheets size={24} className={className} />
    case "dwg":
      return <SiAutocad size={24} className={className} />
    default:
      return <FileText size={24} className={className} />;
  }
}

export const roundOffNumber = (number) => {
  return Math.round(number);
}

export const roundOffToMaxDecimalPlaces = (number, maxDecimalPlaces) => {
  const factor = Math.pow(10, maxDecimalPlaces);
  return Math.round(number * factor) / factor;
}

export const padLeadingZeroes = (number, length) => {
  return number?.toString().padStart(length, "0");
}

export const recordProjectAccess = (projectId) => {
  try {
    const recentIds = JSON.parse(localStorage.getItem("recent_projects_ids") || "[]");
    const updatedIds = [projectId, ...recentIds.filter(id => id !== projectId)].slice(0, 50); // Keep top 50
    localStorage.setItem("recent_projects_ids", JSON.stringify(updatedIds));
  } catch (error) {
    console.error("Error updating recent projects:", error);
  }
};

export const getRecentProjectIds = () => {
  try {
    const recentIds = JSON.parse(localStorage.getItem("recent_projects_ids") || "[]");
    return Array.isArray(recentIds) ? recentIds : [];
  } catch {
    return [];
  }
};

export const getOrgInitials = () => {
  const organization = JSON.parse(localStorage.getItem("organization")) || {};
  const orgInitials = organization.organization_name
    ? organization.organization_name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "SM";
  return orgInitials;
}

export const getFormattedTransactionId = (transaction, type = null) => {
  const organization = JSON.parse(localStorage.getItem("organization")) || {};
  const orgInitials = organization.organization_name
    ? organization.organization_name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "SM";

  const dateStr =
    transaction.created_on ||
    transaction.date ||
    transaction.transaction_date ||
    transaction.payment_date;
  const date = dateStr ? new Date(dateStr) : new Date();
  const year = date.getFullYear();

  const paddedId = String(transaction.id || "").padStart(3, "0");

  // Get category IDs from localStorage
  const materialCategoryId = parseInt(localStorage.getItem("materialCategory"));
  const workerCategoryId = parseInt(localStorage.getItem("workerCategory"));
  const contractorCategoryId = parseInt(
    localStorage.getItem("contractorPaymentCategory"),
  );
  const refundCategoryId = parseInt(localStorage.getItem("refundCategory"));
  const receivedCategoryId = parseInt(localStorage.getItem("receivedCategory"));

  let categoryId = transaction.transaction_catagory_id;

  // If type is provided (from ReceiptPDF), use it
  if (type === "material" || categoryId === materialCategoryId) {
    const poNumber = transaction.po_number;
    const formattedPO = poNumber 
      ? (String(poNumber).startsWith("PO") ? poNumber : `PO-${poNumber}`)
      : `PO-${paddedId}`;
    return `${orgInitials}/${year}/${formattedPO}`;
  }
  if (type === "worker" || categoryId === workerCategoryId) {
    return `${orgInitials}/${year}/WRK/${paddedId}`;
  }
  if (type === "contractor" || categoryId === contractorCategoryId) {
    return `${orgInitials}/${year}/CTR/${paddedId}`;
  }
  if (type === "received" || categoryId === receivedCategoryId) {
    return `${orgInitials}/${year}/RCVD/${paddedId}`;
  }
  if (type === "refund" || categoryId === refundCategoryId) {
    return `${orgInitials}/${year}/RFND/${paddedId}`;
  }
  if (type === "vendor") {
    return `${orgInitials}/${year}/ADV/${paddedId}`;
  }

  // Default / General
  return `${orgInitials}/${year}/GN/${paddedId}`;
};